import { Alert, Badge, Button, Flex, Input, theme } from 'antd';
import styled from 'styled-components';

import BaseText from '@components/typography/BaseText';
import { useCreateCollection, useUpdateCollection } from '@hooks/useCollections';

import ObjectRow from './ObjectRow';
import PanelEyebrow from './PanelEyebrow';

// antd's Badge has no weight prop and its count is not a semantic style slot.
// Tied to antd v6's class name — review on upgrades.
const CountBadge = styled(Badge)`
	.ant-badge-count {
		font-weight: ${({ theme }) => theme.token.fontWeightStrong};
		box-shadow: none;
	}
`;

/**
 * The right panel in builder mode: the name, the picked objects and Save.
 *
 * Owns the save itself. Render it keyed by the edited collection's id: an
 * update's mutation scope is fixed when the hook is created, so a panel must
 * never outlive the collection it was opened for.
 *
 * A collection's objects are fixed once it is created, so editing only renames
 * it: the objects are listed read-only and Update sends the name alone.
 *
 * @param {object} props.builder - `useCollectionBuilder`'s result
 * @param {Array<{ key, label, color?, path }>} props.pickedObjects - the
 *   picked node uuids, resolved
 * @param {string} props.mode - the project type a new collection is created for
 * @param {string} props.savedHint - where a saved collection will appear
 * @param {(collection, { created: boolean, name: string }) => void} props.onSaved - `name`
 *   is the one sent
 * @param {() => void} props.onSaveError
 */
const BuilderPanel = ({ builder, pickedObjects, mode, savedHint, onSaved, onSaveError }) => {
	const { token } = theme.useToken();
	const { mutate: createCollection, isPending: isCreating } = useCreateCollection();
	const { mutate: updateCollection, isPending: isUpdating } = useUpdateCollection(
		builder.editingId
	);

	const { isEditing, isValid, canSave, trimmedName } = builder;
	const isSaving = isCreating || isUpdating;

	const handleSave = () => {
		if (!canSave || isSaving) return;
		const callbacks = { onError: onSaveError };

		if (isEditing) {
			updateCollection(
				{ name: trimmedName },
				{
					...callbacks,
					onSuccess: saved => onSaved(saved, { created: false, name: trimmedName }),
				}
			);
			return;
		}
		createCollection(
			{ name: trimmedName, mode, objects: pickedObjects },
			{
				...callbacks,
				onSuccess: saved => onSaved(saved, { created: true, name: trimmedName }),
			}
		);
	};

	const removeObject = key => builder.setPicked(builder.pickedNodeUuids.filter(k => k !== key));

	return (
		<Flex vertical gap="small" style={{ height: '100%', minHeight: 0 }}>
			<Flex align="center" justify="space-between">
				<PanelEyebrow>{isEditing ? 'EDITING COLLECTION' : 'NEW COLLECTION'}</PanelEyebrow>
				<CountBadge count={pickedObjects.length} showZero color={token.colorPrimary} />
			</Flex>

			<Input
				value={builder.name}
				onChange={event => builder.setName(event.target.value)}
				placeholder="Name this collection..."
				aria-label="Collection name"
				status={trimmedName === '' ? 'warning' : undefined}
			/>
			<BaseText size="xs" type={isValid ? 'secondary' : 'warning'}>
				{isValid
					? savedHint
					: isEditing
						? 'A name is required.'
						: 'A name and at least one object are required.'}
			</BaseText>

			{isEditing && (
				<Alert
					type="info"
					showIcon
					title="A collection’s objects are fixed once it’s created. Create a new collection to use a different set."
				/>
			)}

			<Flex vertical gap="small" style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
				{pickedObjects.length === 0 ? (
					<BaseText
						size="sm"
						type="secondary"
						style={{ marginTop: token.marginXL, textAlign: 'center' }}>
						Tick objects in the tree to add them
					</BaseText>
				) : (
					pickedObjects.map(object => (
						<ObjectRow
							key={object.key}
							object={object}
							onRemove={isEditing ? undefined : removeObject}
						/>
					))
				)}
			</Flex>

			<Flex gap="small">
				<Button block disabled={isSaving} onClick={builder.close}>
					Cancel
				</Button>
				<Button
					block
					type="primary"
					disabled={!canSave}
					loading={isSaving}
					onClick={handleSave}>
					{isEditing ? 'Update collection' : 'Save collection'}
				</Button>
			</Flex>
		</Flex>
	);
};

export default BuilderPanel;
